// src/pages/ProjectedCashFlow.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import API from "../../../api";

// ─── Helper: same month‐label logic as FinanceCharges.jsx ──────────────────
const generateMonthLabels = (startStr, length) => {
  if (!startStr || !startStr.includes("-")) return [];
  const [startM, startY] = startStr.split("-").map(Number);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  let m = startM - 1,
    y = startY;
  return Array.from({ length }, () => {
    const label = `${months[m]}-${y}`;
    m = (m + 1) % 12;
    if (m === 0) y++;
    return label;
  });
};

export default function ProjectedCashFlow() {
  const { projectId } = useParams();

  // ─── State Hooks ─────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  // Common inputs (from API.get)
  const [projectYears, setProjectYears] = useState([]);
  const [capacityUtilization, setCapacityUtilization] = useState([]);
  const [netProfitAfterTax, setNetProfitAfterTax] = useState([]);
  const [projectedProfitabilityInputs, setProjectedProfitabilityInputs] =
    useState({});
  const [depreciationData, setDepreciationData] = useState([]);
  const [termLoanInput, setTermLoanInput] = useState({});

  // CashFlow‐specific state
  const [promotersContribution, setPromotersContribution] = useState(0);
  const [termLoanIndigenous, setTermLoanIndigenous] = useState(0);
  const [sundryCreditorArr, setSundryCreditorArr] = useState([]);
  const [sundryDebtorsArr, setSundryDebtorsArr] = useState([]);
  const [workingCapitalBank, setWorkingCapitalBank] = useState([]);
  const [loanAndAdvancesArr, setLoanAndAdvancesArr] = useState([]);
  const [otherCurrentAssetsArr, setOtherCurrentAssetsArr] = useState([]);
  const [provisionForTaxArr, setProvisionForTaxArr] = useState([]);
  const [promoterDrawingsArr, setPromoterDrawingsArr] = useState([]);
  const [principalRepayments, setPrincipalRepayments] = useState([]);
  const [grossBlock, setGrossBlock] = useState(0);
  const [fixedAssetsInputs, setFixedAssetsInputs] = useState([]);

  // Raw materials & finished goods from DB (for closing stock)
  const [rawMaterialsArr, setRawMaterialsArr] = useState([]);
  const [finishedGoodsArr, setFinishedGoodsArr] = useState([]);

  // ─── Depreciation cost‐lookup helpers (copy from Depreciation.jsx) ─────────
  const getCostByParticular = (p, inputs) => {
    if (!inputs) return 0;
    const {
      costProjectInputs = {},
      plantMachineryData = [],
      plantContingencies,
      plantGstPercent,
      furnitureAndFixtures = [],
      otherFixedAssets = [],
      computers = [],
      preliminaryExpenses = [],
    } = inputs;

    const keyMap = {
      "Land & Site Development (On Lease)": "landDevelopment",
      "Civil Construction (On Lease)": "civilConstruction",
    };
    if (keyMap[p] && costProjectInputs[keyMap[p]] != null) {
      return parseFloat(costProjectInputs[keyMap[p]]) || 0;
    }

    switch (p) {
      case "Plant & Machinery": {
        const subtotal = plantMachineryData.reduce(
          (s, r) => s + (parseFloat(r.amount) || 0),
          0
        );
        const cont = (subtotal * (parseFloat(plantContingencies) || 0)) / 100;
        const tax =
          ((subtotal + cont) * (parseFloat(plantGstPercent) || 0)) / 100;
        return subtotal + cont + tax;
      }
      case "Furniture and Fixture":
        return furnitureAndFixtures.reduce(
          (s, r) => s + (parseFloat(r.amount) || 0),
          0
        );
      case "Miscellaneous Fixed Assets":
        return otherFixedAssets.reduce(
          (s, r) => s + (parseFloat(r.amount) || 0),
          0
        );
      case "Computer and Software":
        return computers.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
      case "Preliminary Expenses":
        return preliminaryExpenses.reduce(
          (s, r) => s + (parseFloat(r.amount) || 0),
          0
        );
      default:
        return 0;
    }
  };

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    API.get(`/user/project-inputs/${projectId}`)
      .then(({ data }) => {
        setData(data);

        // 1. Project Years & Capacity Utilization
        const yrs = Array.isArray(data.projectYears) ? data.projectYears : [];
        setProjectYears(yrs);
        setCapacityUtilization(data.capacityUtilizationPerYear || []);

        // 2. Net Profit After Tax
        const npatArr = Array.isArray(data.netProfitAfterTax)
          ? data.netProfitAfterTax.map((v) => parseFloat(v) || 0)
          : [];
        setNetProfitAfterTax(npatArr);

        // 3. preliminaryExpensesWrittenOff & provisionForTax
        const projInputs = data.projectedProfitabilityInputs || {};
        setProjectedProfitabilityInputs(projInputs);
        const provPct = parseFloat(projInputs.provisionForTax) || 0;
        const provArr = npatArr.map((npt) =>
          provPct > 0 ? +((npt * provPct) / (100 - provPct)).toFixed(2) : 0
        );
        setProvisionForTaxArr(provArr);

        // 4. Depreciation rows & data
        const deprRows =
          Array.isArray(data.depreciationData) && data.depreciationData.length
            ? data.depreciationData
            : [
                "Land & Site Development (On Lease)",
                "Civil Construction (On Lease)",
                "Plant & Machinery",
                "Furniture and Fixture",
                "Miscellaneous Fixed Assets",
                "Computer and Software",
                "Preliminary Expenses",
              ].map((p, i) => ({
                slno: i + 1,
                particular: p,
                taxPercent: 0,
              }));
        setDepreciationData(deprRows);

        // 5. Sundry Creditor (from WorkingCapitalPage)
        setSundryCreditorArr(
          Array.isArray(data.sundryCreditor)
            ? data.sundryCreditor.map((v) => parseFloat(v) || 0)
            : []
        );

        // 6. Sundry Debtors (saved array)
        setSundryDebtorsArr(
          Array.isArray(data.sundryDebtors)
            ? data.sundryDebtors.map((v) => parseFloat(v) || 0)
            : []
        );

        // 7. Working Capital Bank
        setWorkingCapitalBank(
          Array.isArray(data.workingCapitalBank) ||
            Array.isArray(data.workingCapitalBankByYear)
            ? (data.workingCapitalBank || data.workingCapitalBankByYear).map(
                (v) => parseFloat(v) || 0
              )
            : []
        );

        // 8. Raw Materials & Finished Goods (for closing stock)
        setRawMaterialsArr(
          Array.isArray(data.rawMaterials)
            ? data.rawMaterials.map((v) => parseFloat(v) || 0)
            : []
        );
        setFinishedGoodsArr(
          Array.isArray(data.finishedGoods)
            ? data.finishedGoods.map((v) => parseFloat(v) || 0)
            : []
        );

        // 9. Loans & Advances
        const bsInputs = data.balanceSheetInputs || {};
        const principalLA = parseFloat(bsInputs.loanAndAdvances) || 0;
        const pctLA = parseFloat(bsInputs.loanAndAdvancesPct) || 0;
        const laArr = [];
        yrs.forEach((_, i) => {
          laArr[i] =
            i === 0 ? principalLA : +((laArr[i - 1] * pctLA) / 100).toFixed(2);
        });
        setLoanAndAdvancesArr(laArr);

        // 10. Other Current Assets & Promoter Drawings
        setOtherCurrentAssetsArr(
          Array.isArray(data.otherCurrentAssetsByYear)
            ? data.otherCurrentAssetsByYear.map((v) => parseFloat(v) || 0)
            : []
        );
        setPromoterDrawingsArr(
          Array.isArray(data.promoterDrawings)
            ? data.promoterDrawings.map((v) => parseFloat(v) || 0)
            : []
        );

        // 11. Principal Repayments (“Total Principal” from FinanceCharges.jsx)
        const term = data.termLoanInput || {};
        let emiSchedule = Array.isArray(term.emiSchedule)
          ? term.emiSchedule
          : [];
        let summaryBuckets = {};
        if (emiSchedule.length) {
          const rawStart =
            term.pStartMonth && term.pStartMonth.includes("-")
              ? term.pStartMonth
              : term.startMonth || "";
          const monthLabels = generateMonthLabels(rawStart, emiSchedule.length);

          const monthsMap = {
            Jan: 1,
            Feb: 2,
            Mar: 3,
            Apr: 4,
            May: 5,
            Jun: 6,
            Jul: 7,
            Aug: 8,
            Sep: 9,
            Oct: 10,
            Nov: 11,
            Dec: 12,
          };
          emiSchedule.forEach((row, idx) => {
            const lbl = monthLabels[idx] || "";
            const [monStr, yrStr] = lbl.split("-");
            const mon = monthsMap[monStr] || 0;
            const yr = parseInt(yrStr, 10) || 0;
            const fyStart = mon >= 4 ? yr : yr - 1;
            const fyLabel = `${fyStart}-${String(fyStart + 1).slice(-2)}`;
            if (!summaryBuckets[fyLabel]) {
              summaryBuckets[fyLabel] = 0;
            }
            summaryBuckets[fyLabel] += row.principal;
          });
        }
        const prinArr = yrs.map((fy) => +(summaryBuckets[fy] || 0).toFixed(2));
        setPrincipalRepayments(prinArr);

        // 12. Compute “Gross Block” on the fly (sum of costs from depreciationData)
        //     instead of reading a saved depreciationCostTotal
        const costTotal = depreciationData.reduce((sum, row) => {
          return sum + getCostByParticular(row.particular, data);
        }, 0);
        setGrossBlock(+costTotal.toFixed(2));

        // 13. FixedAssetsInputs
        setFixedAssetsInputs(
          Array.isArray(data.projectedCashFlowFixedAssets)
            ? data.projectedCashFlowFixedAssets.map((v) => parseFloat(v) || 0)
            : []
        );

        // 14. termLoanInput (for depreciation’s getMonthsActive)
        setTermLoanInput(data.termLoanInput || {});

        // ─── Compute Promoter’s Contribution & Term Loan from CostOfTheProject.jsx ────────────────────────────
        const {
          plantMachineryData = [],
          plantContingencies = 0,
          plantGstPercent = 0,
          furnitureAndFixtures = [],
          otherFixedAssets = [],
          computers = [],
          preliminaryExpenses = [],
          costProjectInputs: costInputs = {},
          workingCapitalRequirementData: workingCapitalRequirement = [],
        } = data;

        // A) Plant & Machinery grand total
        const pmSubtotal = plantMachineryData.reduce(
          (sum, row) => sum + (Number(row.amount) || 0),
          0
        );
        const pmContAmt = (pmSubtotal * plantContingencies) / 100;
        const pmTaxAmt = ((pmSubtotal + pmContAmt) * plantGstPercent) / 100;
        const plantMachineryGrandTotal = pmSubtotal + pmContAmt + pmTaxAmt;

        // B) Other asset totals
        const furnitureTotal = furnitureAndFixtures.reduce(
          (sum, r) => sum + (parseFloat(r.amount) || 0),
          0
        );
        const miscFixedAssetsTotal = otherFixedAssets.reduce(
          (sum, r) => sum + (parseFloat(r.amount) || 0),
          0
        );
        const computerSoftwareTotal = computers.reduce(
          (sum, r) => sum + (parseFloat(r.amount) || 0),
          0
        );
        const preliminaryExpensesTotal = preliminaryExpenses.reduce(
          (sum, r) => sum + (parseFloat(r.amount) || 0),
          0
        );

        // C) First‐year Working Capital Requirement
        const wcrFirstYear = Array.isArray(workingCapitalRequirement)
          ? workingCapitalRequirement.find((v) => v > 0) || 0
          : 0;

        // D) Direct costInputs fields
        const landDev = parseFloat(costInputs.landDevelopment) || 0;
        const civilConst = parseFloat(costInputs.civilConstruction) || 0;
        const deposits = parseFloat(costInputs.deposits) || 0;
        const termLoanPct = parseFloat(costInputs.termLoanPct) || 0;
        const equityPct = parseFloat(costInputs.equityPct) || 0;
        const wcPct = parseFloat(costInputs.workingCapitalPct) || 0;

        // E) Project Cost (sum of nine line items)
        const projectCost =
          landDev +
          civilConst +
          plantMachineryGrandTotal +
          furnitureTotal +
          miscFixedAssetsTotal +
          computerSoftwareTotal +
          preliminaryExpensesTotal +
          wcrFirstYear +
          deposits;

        // F) Means of Finance
        const workingCapitalTotal = Math.round((wcrFirstYear * wcPct) / 100);
        const termLoanTotal = Math.round(
          (projectCost - wcrFirstYear) * (termLoanPct / 100)
        );
        const equityTotal = projectCost - termLoanTotal - workingCapitalTotal;

        // G) Set Promoter’s Contribution (year 0)
        setPromotersContribution(equityTotal);

        // H) Set Term Loan for Indigenous Assets (year 0)
        setTermLoanIndigenous(termLoanTotal);
      })
      .catch((err) => {
        console.error("Failed to load project inputs:", err);
        setError("Failed to load data.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [projectId, depreciationData]);

  // ─── Depreciation Helpers ────────────────────────────────────────────
  const prod = useMemo(() => {
    const raw = termLoanInput.startMonth || "";
    const [m, y] = raw.split("-").map(Number);
    return m && y ? { month: m, year: y } : null;
  }, [termLoanInput.startMonth]);

  const getMonthsActive = useCallback(
    (fyLabel) => {
      if (!prod) return 12;
      const fyStart = parseInt(fyLabel.slice(0, 4), 10);
      if (isNaN(fyStart)) return 12;
      const fyEnd = fyStart + 1;
      const raw = (fyEnd - prod.year) * 12 + (3 - prod.month + 1);
      return Math.max(0, Math.min(raw, 12));
    },
    [prod]
  );

  const calcFullYear = (cost, rate, yearIndex) => {
    if (yearIndex === 0) return 0;
    let remaining = cost,
      dep = 0;
    for (let i = 1; i <= yearIndex; i++) {
      dep = (remaining * rate) / 100;
      remaining -= dep;
    }
    return dep;
  };

  const depreciationTotals = useMemo(() => {
    if (!data || !projectYears.length) return [];
    const perRowPerYears = depreciationData.map((row) => {
      const cost = getCostByParticular(row.particular, data);
      return projectYears.map((fy, yi) => {
        const full = calcFullYear(cost, row.taxPercent, yi);
        const months = getMonthsActive(fy);
        return +(full * (months / 12)).toFixed(2);
      });
    });
    return projectYears.map((_, yi) =>
      perRowPerYears.reduce((sum, perYrArr) => sum + perYrArr[yi], 0)
    );
  }, [data, projectYears, depreciationData, calcFullYear, getMonthsActive]);

  // ─── Amortised Expenses (exactly as Balance Sheet page) :contentReference[oaicite:0]{index=0} ─────────────────────────────────
  const amortisedExpenses = useMemo(() => {
    if (!data) return [];
    const prelimList = Array.isArray(data.preliminaryExpenses)
      ? data.preliminaryExpenses
      : [];
    const prelimTotal = prelimList.reduce(
      (sum, r) => sum + (parseFloat(r.amount) || 0),
      0
    );
    const prelimWrittenOff =
      parseFloat(projectedProfitabilityInputs.preliminaryExpensesWrittenOff) ||
      0;
    const arr = [];
    projectYears.forEach((_, i) => {
      if (i === 0) {
        arr[i] = prelimTotal;
      } else {
        arr[i] = Math.max((arr[i - 1] || 0) - prelimWrittenOff, 0);
      }
    });
    return arr;
  }, [data, projectYears, projectedProfitabilityInputs]);

  const prelExpAfterTax = useMemo(() => {
    return projectYears.map((_, i) => {
      const profit = netProfitAfterTax[i] || 0;
      const dep = depreciationTotals[i] || 0;
      const prelimAmt =
        i === 0
          ? 0
          : parseFloat(
              projectedProfitabilityInputs.preliminaryExpensesWrittenOff
            ) || 0;
      return +(profit + dep + prelimAmt).toFixed(2);
    });
  }, [
    projectYears,
    netProfitAfterTax,
    depreciationTotals,
    projectedProfitabilityInputs,
  ]);

  const increaseInCreditors = useMemo(
    () =>
      projectYears.map((_, i) =>
        i === 0
          ? sundryCreditorArr[i] || 0
          : +(
              (sundryCreditorArr[i] || 0) - (sundryCreditorArr[i - 1] || 0)
            ).toFixed(2)
      ),
    [projectYears, sundryCreditorArr]
  );

  const termLoanIndigenousArr = useMemo(
    () => projectYears.map((_, i) => (i === 0 ? termLoanIndigenous : 0)),
    [projectYears, termLoanIndigenous]
  );

  const workingCapitalLoan = useMemo(() => {
    const idx = workingCapitalBank.findIndex((v) => v !== 0);
    return projectYears.map((_, i) =>
      i === idx ? workingCapitalBank[idx] : 0
    );
  }, [projectYears, workingCapitalBank]);

  const promotersContributionArr = useMemo(
    () => projectYears.map((_, i) => (i === 0 ? promotersContribution : 0)),
    [projectYears, promotersContribution]
  );

  const cashFlowTotals = useMemo(
    () =>
      projectYears.map(
        (_, i) =>
          +(
            prelExpAfterTax[i] +
            promotersContributionArr[i] +
            increaseInCreditors[i] +
            termLoanIndigenousArr[i] +
            workingCapitalLoan[i]
          ).toFixed(2)
      ),
    [
      projectYears,
      prelExpAfterTax,
      promotersContributionArr,
      increaseInCreditors,
      termLoanIndigenousArr,
      workingCapitalLoan,
    ]
  );

  const loansDiff = useMemo(
    () =>
      projectYears.map((_, i) =>
        i === 0
          ? loanAndAdvancesArr[i] || 0
          : +(
              (loanAndAdvancesArr[i] || 0) - (loanAndAdvancesArr[i - 1] || 0)
            ).toFixed(2)
      ),
    [projectYears, loanAndAdvancesArr]
  );

  const otherAssetsDiff = useMemo(
    () =>
      projectYears.map((_, i) =>
        i === 0
          ? otherCurrentAssetsArr[i] || 0
          : +(
              (otherCurrentAssetsArr[i] || 0) -
              (otherCurrentAssetsArr[i - 1] || 0)
            ).toFixed(2)
      ),
    [projectYears, otherCurrentAssetsArr]
  );

  // ─── Work In Progress: closing stock = rawMaterials + finishedGoods; WIP = current – previous :contentReference[oaicite:1]{index=1} ─────────────────────────────────
  const workInProgressArr = useMemo(() => {
    const closingStockArr = projectYears.map((_, i) => {
      const rm = rawMaterialsArr[i] || 0;
      const fg = finishedGoodsArr[i] || 0;
      return +(rm + fg).toFixed(2);
    });
    return projectYears.map((_, i) =>
      i === 0 ? 0 : +(closingStockArr[i] - closingStockArr[i - 1]).toFixed(2)
    );
  }, [projectYears, rawMaterialsArr, finishedGoodsArr]);

  // ─── Sundry Debtors: current year minus previous year :contentReference[oaicite:2]{index=2} ────────────────────────────────────────
  const sundryDebtorsDiffArr = useMemo(() => {
    return projectYears.map((_, i) =>
      i === 0
        ? sundryDebtorsArr[i] || 0
        : +(
            (sundryDebtorsArr[i] || 0) - (sundryDebtorsArr[i - 1] || 0)
          ).toFixed(2)
    );
  }, [projectYears, sundryDebtorsArr]);

  const movementTotals = useMemo(
    () =>
      projectYears.map(
        (_, i) =>
          +(
            (i === 0 ? grossBlock : fixedAssetsInputs[i - 1] || 0) +
            (principalRepayments[i] || 0) +
            (loansDiff[i] || 0) +
            (otherAssetsDiff[i] || 0) +
            (amortisedExpenses[i] || 0) +
            (provisionForTaxArr[i] || 0) +
            (promoterDrawingsArr[i] || 0) +
            (workInProgressArr[i] || 0) +
            (sundryDebtorsDiffArr[i] || 0)
          ).toFixed(2)
      ),
    [
      projectYears,
      grossBlock,
      fixedAssetsInputs,
      principalRepayments,
      loansDiff,
      otherAssetsDiff,
      amortisedExpenses,
      provisionForTaxArr,
      promoterDrawingsArr,
      workInProgressArr,
      sundryDebtorsDiffArr,
    ]
  );

  const { openingCashArr, netCashInflowArr, closingCashArr } = useMemo(() => {
    const open = [];
    const net = [];
    const close = [];
    projectYears.forEach((_, i) => {
      open[i] = i === 0 ? 0 : close[i - 1];
      net[i] = +(cashFlowTotals[i] - movementTotals[i]).toFixed(2);
      close[i] = +(open[i] + net[i]).toFixed(2);
    });
    return {
      openingCashArr: open,
      netCashInflowArr: net,
      closingCashArr: close,
    };
  }, [projectYears, cashFlowTotals, movementTotals]);

  if (loading)
    return (
      <div className="flex h-64 items-center justify-center">
        <svg
          className="animate-spin h-10 w-10 text-gray-500"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          />
        </svg>
      </div>
    );
  if (error)
    return <div className="text-red-600 text-center py-8">{error}</div>;

  return (
    <div className="p-6 bg-white">
      <h1 className="text-2xl font-semibold mb-4">
        Projected Cash Flow Statement
      </h1>

      {/* ─── First Table ───────────────────────────────────────── */}
      <div className="overflow-x-auto mb-8 shadow-lg rounded-lg">
        <table className="min-w-full table-auto border-collapse">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 border border-gray-200 text-left font-medium">
                Details
              </th>
              {projectYears.map((yr, idx) => (
                <th
                  key={idx}
                  className="px-4 py-2 border border-gray-200 text-center font-medium"
                >
                  {yr}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">
                Capacity Utilization (%)
              </td>
              {capacityUtilization.map((u, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {typeof u === "object" ? u.value : parseFloat(u).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">
                Prel Exp. but after Tax
              </td>
              {prelExpAfterTax.map((v, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">
                Promoter's Contribution
              </td>
              {promotersContributionArr.map((v, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">
                Increase in Creditors
              </td>
              {increaseInCreditors.map((v, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">
                Term Loan for Indigenous Assets
              </td>
              {termLoanIndigenousArr.map((v, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">
                Working Capital Loan
              </td>
              {workingCapitalLoan.map((v, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-200 font-bold">
              <td className="px-4 py-2 border border-gray-200">
                <strong>Total</strong>
              </td>
              {cashFlowTotals.map((v, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  <strong>{v.toFixed(2)}</strong>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Second Table ─────────────────────────────────────── */}
      <h2 className="text-xl font-semibold mb-3">
        Movement of Fixed Assets & Current Assets
      </h2>
      <div className="overflow-x-auto shadow-lg rounded-lg">
        <table className="min-w-full table-auto border-collapse">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 border border-gray-200 text-left font-medium">
                Details
              </th>
              {projectYears.map((yr, idx) => (
                <th
                  key={idx}
                  className="px-4 py-2 border border-gray-200 text-center font-medium"
                >
                  {yr}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">Fixed Assets</td>
              {projectYears.map((_, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {(i === 0
                    ? grossBlock
                    : fixedAssetsInputs[i - 1] || 0
                  ).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">
                Repayment of loan term Loan (Indg. Assets)
              </td>
              {projectYears.map((_, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {(principalRepayments[i] || 0).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">
                Loans & Advances
              </td>
              {loansDiff.map((v, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">
                Other Current Assets
              </td>
              {otherAssetsDiff.map((v, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">
                Amortised Expenses
              </td>
              {amortisedExpenses.map((v, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">
                Payment of Taxes
              </td>
              {provisionForTaxArr.map((v, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">
                Promoters Drawing's
              </td>
              {promoterDrawingsArr.map((v, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">
                Work In Progress
              </td>
              {workInProgressArr.map((v, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">
                Sundry Debtors
              </td>
              {sundryDebtorsDiffArr.map((v, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-200 font-bold">
              <td className="px-4 py-2 border border-gray-200">
                <strong>Total</strong>
              </td>
              {movementTotals.map((v, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  <strong>{v.toFixed(2)}</strong>
                </td>
              ))}
            </tr>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">
                Opening Cash & Bank Balance
              </td>
              {openingCashArr.map((v, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">
                Net Cash Inflow during the year
              </td>
              {netCashInflowArr.map((v, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="even:bg-gray-50">
              <td className="px-4 py-2 border border-gray-200">
                Closing Cash Balance
              </td>
              {closingCashArr.map((v, i) => (
                <td
                  key={i}
                  className="px-4 py-2 border border-gray-200 text-center"
                >
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
